import { Alert, Box, Typography } from '@mui/material';
import ASCSmallCheck from 'components/asc/asc-small-check';
import Breadcrumbs from 'components/breadcrumbs';
import FilterSidebar from 'components/filter-sidebar';
import Paper from 'components/paper';
import Switch from 'components/switch';
import { useAccountState } from 'contexts/account/account-context';
import dayjs from 'dayjs';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import { SiteAutocomplete } from 'views/order-feed/components/site-autocomplete';
import OrderFeedTabsHeader from 'views/order-feed/components/order-feed-tabs-header';
import { OrderFeedSettingsProvider } from 'views/order-feed/context/order-feed-settings-context';
import { OrderFeedProvider } from 'views/order-feed/context/order-feed-context';
import { MySupplyPlanProvider } from 'views/order-feed/features/my-supply-plan/context/my-supply-plan-context';
import { useSelectedSite } from 'views/order-feed/features/my-supply-plan/hooks/use-selected-site';
import InputSettings from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/input-settings/input-settings';
import InventoryConsumptionBox from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/inventory-consumption-box';
import NextDeliveryBox from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/next-delivery-box';
import OfdErrorBoundary from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/ofd-error-boundary';
import OfdSettingsFallback from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/ofd-settings-fallback';
import OrdersSection from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/orders/orders-section';
import SettingsSubTabs from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/settings-sub-tabs';
import OFDTimeline from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/timeline/timeline';
import { InputSettingsProvider, useInputSettingsState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/input-settings-context';
import { OrdersProvider, useOrdersState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/orders-context';
import { OFD2Params } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-2-params.type';
import { SectionTab } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/section-tab.enum';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';

type Props = {};

const today = dayjs();

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan';

const SiteOverview: React.FC<Props> = () => {
  const intl = useIntl();
  const fromDate = today;
  const history = useHistory();
  const toDate = today.add(90, 'days');
  const params = useParams<OFD2Params>();
  const {
    account: { company },
  } = useAccountState();
  const { createTimelineEvent } = useGoogleTagManager();
  const {
    getOfdSettingsQuery: {
      data: ofdSettings,
      isLoading: isOfdSettingsLoading,
      isFetching: isOfdSettingsFetching,
      isError: isOfdSettingsError,
    },
  } = useInputSettingsState();
  const { isAscCompliant, orderSettingsQuery } = useOrdersState();
  const { selectedSite } = useSelectedSite();

  const isInitialOfdSettingsLoading = (isOfdSettingsLoading || isOfdSettingsFetching) && !ofdSettings;
  const isOfdSettingsUnavailable = !isInitialOfdSettingsLoading && (isOfdSettingsError || !ofdSettings);

  const [showTimeline, setShowTimeline] = useState(true);

  return (
    <>
      <OrderFeedTabsHeader
        utils={
          <Breadcrumbs
            crumbs={[
              {
                text: String(company?.addressAccess.find((s) => s.siteErpId === params.siteErpId)?.name),
                url: `${params.siteErpId}/${params.globalSiteId}`,
              },
            ]}
          />
        }
      />
      {!!orderSettingsQuery.data?.isUnderMaintenance && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          <FormattedMessage id={`orderFeed.alert.maintenance`} />
          {orderSettingsQuery.data.endsAt && (
            <Typography sx={{ display: 'flex', alignItems: 'center' }}>
              {intl.formatMessage({ id: 'orderFeed.alert.endsAt' })}
              {dayjs(orderSettingsQuery.data.endsAt).format('L LT')}
            </Typography>
          )}
        </Alert>
      )}
      {isOfdSettingsUnavailable && (
        <Alert severity="warning">
          <FormattedMessage id={'orderFeed.ofdMySupplyPlan.settingsUnavailableMessage'} />
        </Alert>
      )}
      <Box display={'flex'} color={'common.white'} flexDirection={'row'} justifyContent={'space-between'} mt={10} mb={10}>
        <Box display="flex" flexDirection="column" alignItems="flex-start" gap={3}>
          <Typography fontWeight={'bold'}>{intl.formatMessage({ id: `${PAGE_NAME_ID}.ofdActive` })}</Typography>
          {params.globalSiteId && isAscCompliant && <ASCSmallCheck />}
        </Box>
        <Box display={'flex'} flexDirection={'column'} gap={4}>
          <FilterSidebar
            showClearFilter={!!params.globalSiteId || !fromDate.isSame(today) || !toDate.isSame(today.add(90, 'days'))}
            clearFilters={() => history.push('/order-feed/supply-plan')}>
            <SiteAutocomplete
              value={selectedSite || null}
              route={{
                basePath: '/order-feed/supply-plan',
                getPath: (site) =>
                  company?.features.hasOrderFreeDelivery2 && site.isOfd2Enabled
                    ? `/order-feed/supply-plan/order-free-delivery/${site.siteErpId}/${site.globalSiteId}/orders`
                    : `/order-feed/supply-plan/${site.siteErpId}/${site.globalSiteId}`,
              }}
            />
          </FilterSidebar>
        </Box>
      </Box>
      <Box mb={20} display={'flex'} gap={5} color={'common.white'}>
        <OfdSettingsFallback>
          <>
            <InventoryConsumptionBox />
            <NextDeliveryBox />
          </>
        </OfdSettingsFallback>
      </Box>
      <Box display={'flex'} width={'100%'} justifyContent={'space-between'}>
        <SettingsSubTabs />
        <Switch
          sx={{ justifyContent: 'center' }}
          label={'Show timeline'}
          value={showTimeline}
          onChange={() => {
            setShowTimeline((prev) => !prev);
            createTimelineEvent('show_timeline', {
              toggle: !showTimeline,
            });
          }}
        />
      </Box>
      <Paper transparent sx={{ display: 'flex', borderTopLeftRadius: { xs: 0, md: '1rem' }, flexDirection: 'column', gap: 4 }}>
        {showTimeline && (
          <Box display={'flex'} justifyContent={'center'}>
            <Box width={'100%'} borderRadius={2} bgcolor={'common.white'} mb={10}>
              <OFDTimeline />
            </Box>
          </Box>
        )}
        {params.sectionTab === SectionTab.ORDERS && <OrdersSection />}
        {params.sectionTab === SectionTab.INPUT_SETTINGS && <InputSettings />}
      </Paper>
    </>
  );
};

const SiteOverviewContainer: React.FC<{}> = ({ }) => {
  return (
    <OrderFeedProvider>
      <OrderFeedSettingsProvider>
        <MySupplyPlanProvider>
          <InputSettingsProvider>
            <OrdersProvider>
              <OfdErrorBoundary>
                <SiteOverview />
              </OfdErrorBoundary>
            </OrdersProvider>
          </InputSettingsProvider>
        </MySupplyPlanProvider>
      </OrderFeedSettingsProvider>
    </OrderFeedProvider>
  );
};

export default SiteOverviewContainer;
